import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Check, X, Trash2, MessageSquare, Star, Clock, Mail, Users, HelpCircle, Send, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const AdminPage = () => {
  const [tab, setTab] = useState('testimonials');
  const [testimonials, setTestimonials] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingPremiumThanks, setSendingPremiumThanks] = useState(false);
  const [sendingNudge, setSendingNudge] = useState(false);

  const sendPremiumThanks = async () => {
    setSendingPremiumThanks(true);
    try {
      const res = await fetch(`${API}/api/admin/send-premium-thanks`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (res.ok) {
        const names = data.sent_to?.map(u => u.name).join(', ') || 'ninguno';
        toast.success(`Email enviado a ${data.count} usuarios premium: ${names}`);
      } else toast.error('Error al enviar');
    } catch { toast.error('Error de conexion'); }
    setSendingPremiumThanks(false);
  };

  const sendOnboardingNudge = async () => {
    setSendingNudge(true);
    try {
      const res = await fetch(`${API}/api/admin/send-onboarding-nudge`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Nudge enviado a ${data.count} usuarios sin proyectos`);
      } else toast.error('Error al enviar');
    } catch { toast.error('Error de conexion'); }
    setSendingNudge(false);
  };

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/testimonials?approved_only=false`, { headers: getHeaders() });
      const data = await res.json();
      if (Array.isArray(data)) setTestimonials(data);
    } catch { }
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/feedback`, { headers: getHeaders() });
      const data = await res.json();
      if (Array.isArray(data)) setFeedback(data);
    } catch { }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, { headers: getHeaders() });
      const data = await res.json();
      if (Array.isArray(data)) setUsers(data);
    } catch { }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/support/tickets`, { headers: getHeaders() });
      const data = await res.json();
      if (Array.isArray(data)) setTickets(data);
    } catch { }
  }, []);

  useEffect(() => { fetchTestimonials(); fetchFeedback(); fetchUsers(); fetchTickets(); }, [fetchTestimonials, fetchFeedback, fetchUsers, fetchTickets]);

  const approveTestimonial = async (id, approved) => {
    await fetch(`${API}/api/testimonials/${id}/approve`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getHeaders() },
      body: JSON.stringify({ approved })
    });
    toast.success(approved ? 'Testimonio aprobado' : 'Testimonio rechazado');
    fetchTestimonials();
  };

  const deleteTestimonial = async (id) => {
    await fetch(`${API}/api/testimonials/${id}`, { method: 'DELETE', headers: getHeaders() });
    toast.success('Testimonio eliminado');
    fetchTestimonials();
  };

  const deleteFeedback = async (id) => {
    await fetch(`${API}/api/feedback/${id}`, { method: 'DELETE', headers: getHeaders() });
    toast.success('Mensaje eliminado');
    fetchFeedback();
  };

  const typeLabels = { feedback: 'Retroalimentación', suggestion: 'Sugerencia', complaint: 'Reclamación' };
  const typeColors = { feedback: 'text-blue-400 bg-blue-500/10', suggestion: 'text-emerald-400 bg-emerald-500/10', complaint: 'text-red-400 bg-red-500/10' };

  const respondToTicket = async (ticketId) => {
    if (!responseText.trim()) return toast.error('Escribe una respuesta');
    try {
      const res = await fetch(`${API}/api/admin/support/tickets/${ticketId}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ response: responseText })
      });
      if (res.ok) { toast.success('Respuesta enviada (email notificado al usuario)'); setRespondingTo(null); setResponseText(''); fetchTickets(); }
      else toast.error('Error al responder');
    } catch { toast.error('Error de conexion'); }
  };

  const closeTicket = async (ticketId) => {
    await fetch(`${API}/api/admin/support/tickets/${ticketId}/close`, { method: 'POST', headers: getHeaders() });
    toast.success('Ticket cerrado');
    fetchTickets();
  };

  const sendEmailToUser = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) return toast.error('Completa asunto y mensaje');
    try {
      const res = await fetch(`${API}/api/admin/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ to_email: emailTarget.email, subject: emailForm.subject, message: emailForm.message })
      });
      if (res.ok) { toast.success(`Email enviado a ${emailTarget.name}`); setEmailTarget(null); setEmailForm({ subject: '', message: '' }); }
      else toast.error('Error al enviar email');
    } catch { toast.error('Error de conexion'); }
  };

  const ticketStatusColors = { open: 'text-yellow-400 bg-yellow-500/10', responded: 'text-emerald-400 bg-emerald-500/10', closed: 'text-gray-400 bg-gray-500/10' };
  const ticketStatusLabels = { open: 'Abierto', responded: 'Respondido', closed: 'Cerrado' };
  const openTickets = tickets.filter(t => t.status === 'open').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <Card className="bg-gradient-to-r from-card to-secondary/30 border-border/50">
        <CardContent className="p-6">
          <h2 className="text-2xl font-bold mb-1">Panel de Administración</h2>
          <p className="text-muted-foreground">Gestiona testimonios, mensajes y usuarios.</p>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button variant={tab === 'testimonials' ? 'default' : 'outline'} onClick={() => setTab('testimonials')} className="gap-2" data-testid="tab-testimonials">
          <Star className="w-4 h-4" /> Testimonios ({testimonials.length})
        </Button>
        <Button variant={tab === 'feedback' ? 'default' : 'outline'} onClick={() => setTab('feedback')} className="gap-2" data-testid="tab-feedback">
          <MessageSquare className="w-4 h-4" /> Mensajes ({feedback.length})
        </Button>
        <Button variant={tab === 'users' ? 'default' : 'outline'} onClick={() => setTab('users')} className="gap-2" data-testid="tab-users">
          <Users className="w-4 h-4" /> Usuarios ({users.length})
        </Button>
        <Button variant={tab === 'support' ? 'default' : 'outline'} onClick={() => setTab('support')} className="gap-2" data-testid="tab-support">
          <HelpCircle className="w-4 h-4" /> Soporte ({tickets.length})
          {openTickets > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{openTickets}</span>}
        </Button>
      </div>

      {tab === 'testimonials' && (
        <div className="space-y-3">
          {testimonials.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No hay testimonios pendientes.</CardContent></Card>
          ) : testimonials.map(t => (
            <Card key={t.id} className={`overflow-hidden ${t.approved ? 'border-emerald-500/30' : 'border-yellow-500/30'}`} data-testid={`testimonial-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                        {t.name?.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{t.name}</div>
                        <div className="text-xs text-muted-foreground">{t.role}</div>
                      </div>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${t.approved ? 'text-emerald-400 bg-emerald-500/10' : 'text-yellow-400 bg-yellow-500/10'}`}>
                        {t.approved ? 'Aprobado' : 'Pendiente'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground italic">"{t.text}"</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {new Date(t.created_at).toLocaleDateString('es-MX')}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {!t.approved && (
                      <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-400 hover:bg-emerald-500/10" onClick={() => approveTestimonial(t.id, true)} data-testid={`approve-${t.id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    {t.approved && (
                      <Button size="icon" variant="outline" className="h-8 w-8 text-yellow-400 hover:bg-yellow-500/10" onClick={() => approveTestimonial(t.id, false)} data-testid={`reject-${t.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => deleteTestimonial(t.id)} data-testid={`delete-testimonial-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === 'feedback' && (
        <div className="space-y-3">
          {feedback.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No hay mensajes recibidos.</CardContent></Card>
          ) : feedback.map(f => (
            <Card key={f.id} data-testid={`feedback-${f.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="font-semibold text-sm">{f.name}</div>
                      <a href={`mailto:${f.email}`} className="text-xs text-blue-400 flex items-center gap-1 hover:underline">
                        <Mail className="w-3 h-3" /> {f.email}
                      </a>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[f.type] || 'text-gray-400 bg-gray-500/10'}`}>
                        {typeLabels[f.type] || f.type}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{f.message}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {new Date(f.created_at).toLocaleDateString('es-MX')}
                    </div>
                  </div>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0" onClick={() => deleteFeedback(f.id)} data-testid={`delete-feedback-${f.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-3">
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="gap-2" onClick={sendOnboardingNudge} disabled={sendingNudge} data-testid="send-nudge-btn">
              <HelpCircle className="w-4 h-4" /> {sendingNudge ? 'Enviando...' : 'Nudge sin proyectos'}
            </Button>
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={sendPremiumThanks} disabled={sendingPremiumThanks} data-testid="send-premium-thanks-btn">
              <Mail className="w-4 h-4" /> {sendingPremiumThanks ? 'Enviando...' : 'Agradecer a Premium'}
            </Button>
          </div>
          {users.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No hay usuarios registrados.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-border/50 bg-muted/30">
                        <th className="text-left p-3 font-medium text-muted-foreground">Nombre</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Email</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Plan</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Proyectos</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Registrado</th>
                        <th className="text-center p-3 font-medium text-muted-foreground">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr key={u.user_id || i} className="border-b border-border/30 hover:bg-muted/20" data-testid={`user-row-${i}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                                  {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                              )}
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">{u.email}</td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              u.plan === 'annual' ? 'text-emerald-400 bg-emerald-500/10' :
                              u.plan === 'monthly' ? 'text-blue-400 bg-blue-500/10' :
                              'text-gray-400 bg-gray-500/10'
                            }`}>
                              {u.plan === 'annual' ? 'Anual' : u.plan === 'monthly' ? 'Mensual' : 'Gratis'}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`inline-block min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold ${
                              u.project_count > 0 ? 'text-blue-400 bg-blue-500/10' : 'text-gray-500 bg-gray-500/10'
                            }`} data-testid={`user-projects-${i}`}>
                              {u.project_count || 0}
                            </span>
                          </td>
                          <td className="p-3 text-muted-foreground text-xs">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td className="p-3 text-center">
                            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-blue-400 hover:text-blue-300" onClick={() => { setEmailTarget(u); setEmailForm({ subject: '', message: '' }); }} data-testid={`email-user-${i}`}>
                              <Mail className="w-3 h-3" /> Email
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === 'support' && (
        <div className="space-y-3">
          {tickets.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No hay tickets de soporte.</CardContent></Card>
          ) : tickets.map(t => (
            <Card key={t.id} className={`overflow-hidden ${t.status === 'open' ? 'border-yellow-500/30' : t.status === 'responded' ? 'border-emerald-500/30' : 'border-border/30'}`} data-testid={`support-ticket-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold text-xs">
                        {t.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{t.user_name}</div>
                        <div className="text-xs text-muted-foreground">{t.user_email}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ticketStatusColors[t.status]}`}>
                        {ticketStatusLabels[t.status]}
                      </span>
                      <span className="text-xs text-muted-foreground px-1.5 py-0.5 rounded bg-muted">{t.category}</span>
                    </div>
                    <h4 className="font-medium text-sm mb-1">{t.subject}</h4>
                    <p className="text-sm text-muted-foreground">{t.message}</p>
                    {t.responses?.map(r => (
                      <div key={r.id} className="mt-2 ml-4 pl-3 border-l-2 border-emerald-500/30">
                        <div className="flex items-center gap-2 mb-0.5">
                          <CheckCircle className="w-3 h-3 text-emerald-400" />
                          <span className="text-xs font-medium text-emerald-400">{r.responded_by}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString('es-MX')}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{r.message}</p>
                      </div>
                    ))}
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" /> {new Date(t.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    {respondingTo === t.id && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-20 px-3 py-2 rounded-md border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="Escribe tu respuesta..."
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          data-testid="ticket-response-input"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => respondToTicket(t.id)} data-testid="ticket-response-send">
                            <Send className="w-3 h-3" /> Enviar
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setRespondingTo(null); setResponseText(''); }}>Cancelar</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {t.status !== 'closed' && (
                      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => { setRespondingTo(t.id); setResponseText(''); }} data-testid={`respond-ticket-${t.id}`}>
                        <Send className="w-3 h-3" /> Responder
                      </Button>
                    )}
                    {t.status !== 'closed' && (
                      <Button size="icon" variant="outline" className="h-8 w-8 text-gray-400 hover:bg-gray-500/10" onClick={() => closeTicket(t.id)} data-testid={`close-ticket-${t.id}`} title="Cerrar ticket">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Email Modal */}
      {emailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEmailTarget(null)}>
          <Card className="w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()} data-testid="email-modal">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2"><Mail className="w-5 h-5 text-blue-400" /> Enviar Email</h3>
                <button onClick={() => setEmailTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-muted-foreground">Para: <strong>{emailTarget.name}</strong> ({emailTarget.email})</p>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Asunto</label>
                <input
                  className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Asunto del correo"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Mensaje</label>
                <textarea
                  className="w-full h-32 px-3 py-2 rounded-md border border-border bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Escribe tu mensaje..."
                  value={emailForm.message}
                  onChange={(e) => setEmailForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="email-message-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEmailTarget(null)}>Cancelar</Button>
                <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={sendEmailToUser} data-testid="email-send-btn">
                  <Send className="w-4 h-4" /> Enviar Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
